import { action, computed, observable } from "mobx";

import { newGuid } from "@bps/common-utils";
import { DateTime } from "@bps/date-time";
import {
  ClinicalDataType,
  ObservationDto,
  ObservationType
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { ClinicalStore } from "@stores/clinical/ClinicalStore.ts";
import { ClinicalDataTypeName } from "@stores/clinical/models/clinical-tab/ClinicalTabName.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";

import { ClinicalDataToObservationConverter } from "../utils/ClinicalDataToObservationConverter.ts";

export class ClinicalToolResultOpener {
  @observable
  private _showConfirmDialog: boolean;
  private _openResultType: ClinicalDataType;
  private _converter = new ClinicalDataToObservationConverter();
  private _openContextId: string | undefined;

  constructor(
    private clinical: ClinicalStore,
    private clinicalRecord: ClinicalRecord
  ) {
    if (!clinical) throw new Error("clinical required parameter");

    if (!clinicalRecord) throw new Error("clinicalRecord required parameter");
  }

  @computed
  get showConfirmDialog() {
    return this._showConfirmDialog;
  }

  @computed
  get resultTypeName() {
    return ClinicalDataTypeName[this._openResultType];
  }

  @action
  openClinicalToolFromDropDown = (type: ClinicalDataType) => {
    if (type === ClinicalDataType.PSFS) {
      this.closeConfirmDialog();
      const contextId = newGuid();
      const encounterId = this.clinicalRecord?.openEncounter?.id;
      this.clinical.ui.setPatientClinicalContent({
        clinicalRecord: this.clinicalRecord,
        type,
        contextId,
        encounterId
      });
    } else {
      this._openResultType = type;
      this._openContextId = undefined;

      const observation = this.clinicalRecord.observations.find(
        m =>
          m.encounterId === this.clinicalRecord.openEncounter?.id &&
          m.type === this._converter.clinicalToObservation(type)
      );

      if (observation) {
        this._openContextId = observation.values[0].contextId;
        this._showConfirmDialog = true;
      } else {
        this.closeConfirmDialog();
        this.clinical.ui.setPatientClinicalContent({
          clinicalRecord: this.clinicalRecord,
          type
        });
      }
    }
  };

  //after the user has confirmed to open the current result from the modal...
  @action
  onConfirmOpenCurrentResult = () => {
    this.closeConfirmDialog();
    this.clinical.ui.setPatientClinicalContent({
      clinicalRecord: this.clinicalRecord,
      type: this._openResultType,
      contextId: this._openContextId
    });
  };

  @action
  closeConfirmDialog = () => {
    this._showConfirmDialog = false;
  };

  openResultFromLink = async (item: ObservationDto) => {
    if (item.type === ObservationType.PSFS) {
      const psfsContext =
        this.clinicalRecord.clinicalData?.psfsContext?.contexts.find(
          x => x.contextId === item.values[0].contextId
        );

      const converterType =
        this.clinicalRecord.openEncounter?.id === item.encounterId
          ? this._converter.observationToClinical(ObservationType[item.type])
          : this._converter.observationToReadOnlyView(
              ObservationType[item.type]
            );

      this.clinical.ui.setPatientClinicalContent({
        clinicalRecord: this.clinicalRecord,
        type: converterType,
        encounterId: item.encounterId,
        contextId: item.values[0].contextId,
        title: `${item.type} ${psfsContext?.diagnosis?.originalText}`,
        toolTipText: `${item.type} ${psfsContext?.diagnosis
          ?.originalText} - ${DateTime.fromISOOrNow(
          item.changeLog?.createdDate
        ).toDayDefaultFormat()}`
      });
    } else if (this.clinicalRecord.openEncounter?.id === item.encounterId) {
      //open for current encounter
      this.clinical.ui.setPatientClinicalContent({
        clinicalRecord: this.clinicalRecord,
        type: this._converter.observationToClinical(ObservationType[item.type])
      });
    } else {
      //open historical entry
      this.clinical.ui.setPatientClinicalContent({
        clinicalRecord: this.clinicalRecord,
        type: this._converter.observationToReadOnlyView(
          ObservationType[item.type]
        ),
        encounterId: item.encounterId,
        contextId: item.values[0].contextId,
        title: `${item.type} - ${DateTime.fromISOOrNow(
          item.changeLog?.createdDate
        ).toDayDefaultFormat()}`
      });
    }
  };

  @action
  addNewSeriesItemFromLink = (type: ClinicalDataType, contextId: string) => {
    this._openResultType = type;
    this._openContextId = contextId;

    const typeText = this._converter.clinicalToObservation(type);
    const observation = this.clinicalRecord.observations.find(
      m =>
        m.encounterId === this.clinicalRecord.openEncounter?.id &&
        m.type === typeText &&
        m.values[0].contextId === contextId
    );

    if (observation) {
      this._showConfirmDialog = true;
    } else if (type === ClinicalDataType.PSFS) {
      const psfsContext =
        this.clinicalRecord.clinicalData?.psfsContext?.contexts.find(
          x => x.contextId === contextId
        );

      const psfsObservation = this.clinicalRecord.observations.find(
        x => x.values[0].contextId === contextId && x.type === typeText
      );

      this.clinical.ui.setPatientClinicalContent({
        clinicalRecord: this.clinicalRecord,
        type,
        contextId,
        encounterId: psfsObservation?.encounterId,
        title: `${ObservationType.PSFS} ${psfsContext?.diagnosis?.originalText}`,
        toolTipText: `${ObservationType.PSFS} ${psfsContext?.diagnosis
          ?.originalText} - ${DateTime.now().toDayDefaultFormat()}`
      });
    } else {
      this.closeConfirmDialog();
      this.clinical.ui.setPatientClinicalContent({
        clinicalRecord: this.clinicalRecord,
        type,
        contextId
      });
    }
  };
}
